import { AuthService, UserRoleBasedRedirectService } from '@common/auth/services/index';
import { User, IUser, EmailMessage, EmailType } from '@koomzo/commonmodel';
import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { BaseFormComponent } from '@shared/components/core/base-form.component';
import { ActivatedRoute } from '@angular/router';
import { IPersona } from '@app/shared/models/index';
import { TermsOfServicePage } from '@app/common/terms-of-service/terms-of-service.page';
import { PrivacyPolicyPage } from '@app/common/privacy-policy/privacy-policy.page';
import { IonRouterOutlet } from '@ionic/angular';
import { LogIn, UpdatePushToken } from '@app/common/auth/store/auth.actions';
import * as utils from '@koomzo/commonutil';
import { Actions, ofActionSuccessful } from '@ngxs/store';
import { UsersDbService } from '@app/shared/services/index';
import { SetCurrentTenant } from '@app/shared/store/app.actions';
import { inProviderSpace } from '@common/auth/models/session-role';

@Component({
    selector: 'signup-form',
    templateUrl: './signup-form.component.html',
    styleUrls: ['./signup-form.component.scss'],
    standalone: false
})
export class SignupFormComponent extends BaseFormComponent<any> implements OnInit {
  @Input() showSocialLogin: boolean;
  isAdminApp: boolean = this.appEnv.isAdmin;

  @Output()
  submitted = new EventEmitter<User>();

  persona: IPersona;

  constructor(
    private actions: Actions,
    protected userDbService: UsersDbService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private routerOutlet: IonRouterOutlet,
    private loginRedirectService: UserRoleBasedRedirectService
  ) {
    super();
    this.title = 'Sign Up';

    this.route.queryParams.subscribe(params => {
      if (this.router.getCurrentNavigation().extras.state) {
        this.persona = this.router.getCurrentNavigation().extras.state.personaCode;
      }
    });
  }

  ngOnInit() {
    this.formModel = this.commonFormModels.getUserSignupFormModel();

    this.actions
      .pipe(ofActionSuccessful(LogIn))
      .subscribe(() => (inProviderSpace(this.currentUser) ? this.navCtrl.navigateForward(['/provider']) : this.navCtrl.navigateForward(['/consumer'])));

      this.actions.pipe(ofActionSuccessful(LogIn)).subscribe(() => {
        const pushToken = this.store.selectSnapshot(state => state.app.pushToken);
  
        // only update user push notification token if it has changed
        if (pushToken !== this.currentUser.pushToken) {
          console.log('push notfication token changed');
          this.store.dispatch(new UpdatePushToken({ id: this.currentUser.id, pushToken: pushToken }));
        }
        // a tenant member (any role) loads the current tenant now, as the login page does
        if (this.currentUser.tenantId) {
          this.store.dispatch(new SetCurrentTenant({ id: this.currentUser.tenantId }));
        }  
        this.loginRedirectService.redirectUser(this.appEnv, this.navCtrl, this.currentUser, this.userRoleRouteRedirectPathMappingList);
      });


    this.modelMap = this.dynamicFormsService.getformModelMap(this.formModel);
    this.form = this.dynamicFormsService.createFormGroup(this.formModel);
  }

  ionViewWillEnter() {
    this.menuCtrl.enable(false);
  }

  /**rollback your custom splitspane status when leaving page */
  public ionViewWillLeave(): void {
    this.blurActiveElement();
    this.menuCtrl.enable(true);
  }

  goToLogin() {
    this.blurActiveElement();
    this.navCtrl.navigateRoot('/login');
  }

  //override base onSubmit
  async onSubmit(value) {
    const userModel = new User({ ...value, authProviderId: 'password' });
    //set password from ui password group
    const password = value.passwordGroup.password;
    userModel.password = password;

    if (this.appEnv.usernameStrategy === 'mobile') {
      userModel.username = userModel.mobile;
    }

    if (this.appEnv.usernameStrategy === 'email') {
      userModel.username = userModel.email;
    }

    if (!userModel.language) userModel.language = this.languageService.activeLang;

    userModel.initConsumerUser();
    try {
      //create and signin global consumer user, these users are not associated with any tenant
      userModel.execute();

      const userDbModel = await this.authService.register(userModel, this.appDomain);
      //send email
      this.sendEmail(userModel, password, 'welcome');
      const username = utils.formatUsernameToEmailFormat(userModel.username, this.appDomain);
      this.store.dispatch(new LogIn({ username, password }));
    } catch (error) {
      console.log('new user created with errors');
      console.log(error);
    }
  }

  //override base onSubmit
  async onSubmitTest(value) {
    
    const mobileNo =  15717781000+ Math.floor(Math.random() * 1000);
    const mobile=`+1${mobileNo}`
    value = {
      language: 'en',
      lastname: 'lastname_admin1',
      givennames: 'first_admin1',
      //email: 'glongchi@gmail.com',
      email: `glongchi@gmail.com`,
      mobile: mobile,
      username:mobile,
      passwordGroup: {
        password: 'password',
        confirmPassword: 'password'
      },
      isAnEnterprise: false
    };

    const userModel = new User({ ...value, authProviderId: 'password' });
    //set password from ui password group
    const password = value.passwordGroup.password;
    userModel.password = password;

    if (this.appEnv.usernameStrategy === 'mobile') {
      userModel.username = userModel.mobile;
    }

    if (this.appEnv.usernameStrategy === 'email') {
      userModel.username = userModel.email;
    }

    if (!userModel.language) userModel.language = this.languageService.activeLang;

    userModel.initConsumerUser();
    try {

      
      console.log('NEW USER CREATED:',userModel.mobile);

      //create and signin global consumer user, these users are not associated with any tenant
      userModel.execute();

      const userDbModel = await this.authService.register(userModel, this.appDomain);

      //send email
      this.sendEmail(userModel, password, 'welcome');

      const username = utils.formatUsernameToEmailFormat(userModel.username, this.appDomain);
      this.store.dispatch(new LogIn({ username, password }));
    } catch (error) {
      console.log('new user created with errors');
      console.log(error);
    }
  }

  async showTermsModal() {
    this.blurActiveElement();
    const modal = await this.modalCtrl.create({
      component: TermsOfServicePage,
      presentingElement: this.routerOutlet.nativeEl
    });
    return await modal.present();
  }

  async showPrivacyModal() {
    this.blurActiveElement();
    const modal = await this.modalCtrl.create({
      component: PrivacyPolicyPage,
      presentingElement: this.routerOutlet.nativeEl
    });
    return await modal.present();
  }

  private sendEmail(userModel: IUser, password: string, emailSubj: string): void {
    const newUserWelcomeEmail = new EmailMessage({
      emailType: EmailType.newUserWelcomeEmail,
      lastname: userModel.lastname,
      language: userModel.language,
      username: userModel.username,
      password: password,
      receiverName:userModel.lastname,
      text:'',
      to: [userModel.email],
      subject: emailSubj
    });
    this.emailService.sendNewUserWelcomeEmail(newUserWelcomeEmail);
  }

  private blurActiveElement(): void {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }
}
